import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Sparkles } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

const Navbar = ({ onOpenAudit }) => {
  const { t, lang, toggle } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: "/", label: t.nav.home },
    { to: "/about", label: t.nav.about },
    { to: "/services", label: t.nav.services },
    { to: "/portfolio", label: t.nav.portfolio },
    { to: "/blog", label: t.nav.blog },
    { to: "/contact", label: t.nav.contact },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled || open
          ? "bg-brand-bg/85 backdrop-blur-xl border-b border-brand-border"
          : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <div className="flex items-center justify-between h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2 group">
            <div className="relative w-8 h-8 grid place-items-center">
              <div className="absolute inset-0 rounded-full bg-gradient-to-br from-brand-purple to-brand-pink blur-md opacity-60 group-hover:opacity-100 transition-opacity" />
              <div className="relative w-7 h-7 rounded-full bg-gradient-to-br from-brand-purple to-brand-pink grid place-items-center text-white font-heading font-black text-sm">
                P
              </div>
            </div>
            <span className="font-heading font-black tracking-tighter text-base md:text-xl text-white">
              PRIME<span className="text-white">/</span>COLLECTIVE
            </span>
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-link-${link.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-[12px] font-medium uppercase tracking-[0.18em] transition-colors link-slide ${
                    isActive ? "text-brand-purpleLight" : "text-zinc-300 hover:text-white"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="lang-toggle"
              onClick={toggle}
              className="hidden sm:flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-zinc-300 hover:text-white transition-colors px-3 py-2 rounded-full border border-brand-border hover:border-brand-purple"
              aria-label="Toggle language"
            >
              <span className={lang === "bg" ? "text-brand-purpleLight" : ""}>BG</span>
              <span className="text-zinc-600">/</span>
              <span className={lang === "en" ? "text-brand-purpleLight" : ""}>EN</span>
            </button>

            <button
              data-testid="nav-audit-button"
              onClick={onOpenAudit}
              className="hidden md:inline-flex items-center gap-2 btn-holo text-white font-bold uppercase tracking-wide text-[11px] px-5 py-3 rounded-full"
            >
              <Sparkles className="w-3.5 h-3.5" />
              {lang === "bg" ? "Безплатен Audit" : "Free Audit"}
            </button>

            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden p-2 text-white"
              aria-label="Menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-brand-border bg-brand-bg/95 backdrop-blur-xl">
          <nav className="px-6 py-8 flex flex-col gap-1">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`mobile-nav-link-${link.to.replace("/", "") || "home"}`}
                className={({ isActive }) =>
                  `text-2xl font-heading uppercase tracking-tight py-3 border-b border-brand-border ${
                    isActive ? "text-white" : "text-white"
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <button
              data-testid="mobile-lang-toggle"
              onClick={toggle}
              className="mt-6 self-start flex items-center gap-2 text-sm font-mono uppercase tracking-[0.2em] text-zinc-300 px-4 py-2 rounded-full border border-brand-border"
            >
              <span className={lang === "bg" ? "text-brand-purpleLight" : ""}>BG</span>
              <span className="text-zinc-600">/</span>
              <span className={lang === "en" ? "text-brand-purpleLight" : ""}>EN</span>
            </button>
            <button
              data-testid="mobile-audit-button"
              onClick={() => { setOpen(false); onOpenAudit(); }}
              className="mt-4 inline-flex items-center justify-center gap-2 btn-holo text-white font-bold uppercase tracking-wide text-xs px-5 py-4 rounded-full"
            >
              <Sparkles className="w-4 h-4" />
              {lang === "bg" ? "Безплатен Audit" : "Free Audit"}
            </button>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
